import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  Typography,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  Divider,
  Alert,
  Breadcrumbs,
  Link,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  getAnimalById,
  getAnimalFormOptions,
  updateAnimal,
  searchDams,
  searchSires,
} from "../../api/animal";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";

const GENDER_OPTIONS = ["Male", "Female"];
const CATEGORY_OPTIONS = ["Heifer", "Steer", "Bull", "Cow", "Calf"];
const STATUS_OPTIONS = ["On Farm", "Sold", "Deceased", "Transferred"];
const MIN_QUERY = 1;

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

function normalizeGender(v) {
  if (!v) return "";
  const s = String(v).trim();
  const hit = GENDER_OPTIONS.find(
    (o) => o.toLowerCase() === s.toLowerCase()
  );
  return hit || s;
}

function normalizeCategory(v) {
  if (!v) return "";
  const s = String(v).trim();
  const hit = CATEGORY_OPTIONS.find(
    (o) => o.toLowerCase() === s.toLowerCase()
  );
  return hit || s;
}

function normalizeStatus(v) {
  if (!v) return "";
  const s = String(v).trim().replace(/_/g, " ");
  const hit = STATUS_OPTIONS.find(
    (o) => o.toLowerCase().replace(/\s/g, "") ===
      s.toLowerCase().replace(/\s/g, "")
  );
  return hit || s;
}

function mapAnimalToFormValues(animal) {
  return {
    tag_number: animal.tag_number ?? "",
    alias: animal.alias ?? "",
    pattern_markings: animal.pattern_markings ?? "",
    breed: animal.breed ?? "",
    description: animal.description ?? "",
    dob: animal.dob ? new Date(animal.dob) : null,
    gender: normalizeGender(animal.gender),
    category: normalizeCategory(animal.category),
    status: normalizeStatus(animal.status) || "On Farm",
    weight: animal.weight != null && animal.weight !== "" ? String(animal.weight) : "",
    is_active: Boolean(
      animal.is_active === undefined || animal.is_active === null
        ? true
        : animal.is_active
    ),
    dam_id: animal.dam_id ?? null,
    sire_id: animal.sire_id ?? null,
    kraal_name: animal.kraal_name ?? "",
    tags: Array.isArray(animal.tags)
      ? [...animal.tags]
      : animal.tags
        ? [animal.tags]
        : [],
  };
}

const emptyForm = {
  tag_number: "",
  alias: "",
  pattern_markings: "",
  breed: "",
  description: "",
  dob: null,
  gender: "",
  category: "",
  status: "On Farm",
  weight: "",
  is_active: true,
  dam_id: null,
  sire_id: null,
  kraal_name: "",
  tags: [],
};

export default function AnimalEdit() {
  const { id: idParam } = useParams();
  const animalId = Number(idParam);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );
  const rawTenantId = searchParams.get("tenant_id");
  const tidNum =
    rawTenantId != null && rawTenantId !== "" ? Number(rawTenantId) : null;
  const farmTenantId =
    tidNum != null && Number.isFinite(tidNum) ? tidNum : null;
  const profileQs =
    farmTenantId != null ? `?tenant_id=${farmTenantId}` : "";
  const apiFarmTenant = isSuperAdmin ? farmTenantId : null;
  const animalsListQs = profileQs;

  const [loadedAnimal, setLoadedAnimal] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [damQuery, setDamQuery] = useState("");
  const [damOptions, setDamOptions] = useState([]);
  const [damLoading, setDamLoading] = useState(false);
  const [sireQuery, setSireQuery] = useState("");
  const [sireOptions, setSireOptions] = useState([]);
  const [sireLoading, setSireLoading] = useState(false);
  const [kraalOptions, setKraalOptions] = useState([]);
  const [breedOptions, setBreedOptions] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [damSelected, setDamSelected] = useState(null);
  const [sireSelected, setSireSelected] = useState(null);

  useEffect(() => {
    if (!Number.isFinite(animalId)) {
      setLoadError("Invalid animal ID.");
      setPageLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setPageLoading(true);
      setLoadError(null);
      try {
        if (isSuperAdmin && farmTenantId == null) {
          setLoadedAnimal(null);
          setLoadError(null);
          setPageLoading(false);
          return;
        }
        const row = await getAnimalById(animalId, apiFarmTenant);
        if (cancelled) return;

        const [damObj, sireObj] = await Promise.all([
          row?.dam_id
            ? getAnimalById(row.dam_id, apiFarmTenant).catch(() => null)
            : Promise.resolve(null),
          row?.sire_id
            ? getAnimalById(row.sire_id, apiFarmTenant).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;

        setLoadedAnimal(row);
        setDamSelected(damObj || null);
        setSireSelected(sireObj || null);
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e?.message || e?.detail || "Could not load this animal."
          );
          setLoadedAnimal(null);
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [animalId, apiFarmTenant, farmTenantId, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && farmTenantId == null) return;
    let active = true;
    (async () => {
      const opts = await getAnimalFormOptions(apiFarmTenant);
      if (!active) return;
      setKraalOptions(opts.kraal_names);
      setBreedOptions(opts.breed_suggestions);
      setTagSuggestions(opts.tag_suggestions);
    })();
    return () => {
      active = false;
    };
  }, [isSuperAdmin, farmTenantId, apiFarmTenant]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!damQuery || damQuery.trim().length < MIN_QUERY) {
        if (active) setDamOptions([]);
        return;
      }
      setDamLoading(true);
      try {
        const rows = await searchDams(damQuery.trim(), apiFarmTenant);
        if (active) setDamOptions(rows);
      } finally {
        if (active) setDamLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [damQuery, apiFarmTenant]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!sireQuery || sireQuery.trim().length < MIN_QUERY) {
        if (active) setSireOptions([]);
        return;
      }
      setSireLoading(true);
      try {
        const rows = await searchSires(sireQuery.trim(), apiFarmTenant);
        if (active) setSireOptions(rows);
      } finally {
        if (active) setSireLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [sireQuery, apiFarmTenant]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: loadedAnimal
      ? mapAnimalToFormValues(loadedAnimal)
      : emptyForm,
    validationSchema: Yup.object({
      tag_number: Yup.string().required("Tag number is required"),
      breed: Yup.string().max(50).required("Breed is required"),
      dob: Yup.date()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        )
        .max(new Date(), "Date cannot be in the future"),
      gender: Yup.string().required("Gender is required"),
      category: Yup.string()
        .required("Category is required")
        .test(
          "matches-gender",
          "Bull and Steer are male; Cow and Heifer are female. Choose a category that matches gender (Calf can be either).",
          function categoryMatchesGender(val) {
            const gender = this.parent.gender;
            if (!val || !gender) return true;
            const g = String(gender).toLowerCase();
            const c = String(val).toLowerCase();
            if (g === "female" && (c === "bull" || c === "steer")) return false;
            if (g === "male" && (c === "cow" || c === "heifer")) return false;
            return true;
          }
        ),
      status: Yup.string().required("Status is required"),
      weight: Yup.number()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue == null ? undefined : value
        )
        .nullable()
        .integer("Use whole kilograms")
        .min(0, "Weight must be positive")
        .optional(),
      kraal_name: Yup.string().max(50).optional(),
      tags: Yup.array().of(Yup.string()),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      if (!loadedAnimal?.id) return;
      try {
        const payload = {
          tag_number: values.tag_number.trim(),
          alias: values.alias?.trim() || undefined,
          pattern_markings: values.pattern_markings?.trim() || undefined,
          breed: (values.breed || "").trim(),
          description: values.description?.trim() || undefined,
          dob: values.dob
            ? values.dob.toISOString
              ? values.dob.toISOString()
              : values.dob
            : null,
          gender: values.gender,
          category: values.category,
          status: values.status,
          is_active: values.is_active,
          dam_id: values.dam_id ?? undefined,
          sire_id: values.sire_id ?? undefined,
          tags: values.tags?.length ? values.tags : [],
        };

        if (values.weight === "" || values.weight == null) {
          delete payload.weight;
        } else {
          payload.weight = Number(values.weight);
        }

        const kn = (values.kraal_name || "").trim();
        if (!kn) delete payload.kraal_name;
        else payload.kraal_name = kn;

        await updateAnimal(loadedAnimal.id, payload, apiFarmTenant);
        enqueueSnackbar("Animal updated successfully.", { variant: "success" });
        navigate(`/animal/${loadedAnimal.id}${profileQs}`);
      } catch (error) {
        const data =
          typeof error === "object" && error !== null && !error.response
            ? error
            : error?.response?.data;
        if (data?.errors) {
          const serverErrors = {};
          Object.keys(data.errors).forEach((k) => {
            serverErrors[k] = data.errors[k][0];
          });
          setErrors(serverErrors);
        }
        enqueueSnackbar(
          (typeof data === "string" ? data : null) ||
            data?.detail ||
            data?.message ||
            (typeof error === "string" ? error : null) ||
            error?.message ||
            "Update failed.",
          { variant: "error" }
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getParentLabel = (opt) =>
    opt ? `${opt.tag_number}${opt.alias ? ` • ${opt.alias}` : ""}` : "";

  const damOptionsMerged = useMemo(() => {
    const byId = new Map(damOptions.map((o) => [o.id, o]));
    if (damSelected?.id != null) byId.set(damSelected.id, damSelected);
    return Array.from(byId.values());
  }, [damOptions, damSelected]);

  const sireOptionsMerged = useMemo(() => {
    const byId = new Map(sireOptions.map((o) => [o.id, o]));
    if (sireSelected?.id != null) byId.set(sireSelected.id, sireSelected);
    return Array.from(byId.values());
  }, [sireOptions, sireSelected]);

  if (isSuperAdmin && farmTenantId == null) {
    return (
      <Box sx={{ p: 3, maxWidth: 560 }}>
        <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link component={RouterLink} underline="hover" color="inherit" to="/animals">
            Animals
          </Link>
          <Typography color="text.primary">Edit animal</Typography>
        </Breadcrumbs>
        <Alert severity="info" sx={{ mb: 2 }}>
          As a platform admin, open the animal list, select an organization, then
          use edit from the grid so the correct <code>tenant_id</code> is included
          in the URL.
        </Alert>
        <Button variant="contained" component={RouterLink} to="/animals">
          Go to animal list
        </Button>
      </Box>
    );
  }

  if (pageLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !loadedAnimal) {
    return (
      <Box sx={{ p: 3, maxWidth: 720 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={`/animals${animalsListQs}`}
          >
            Animals
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>
        <Alert severity="error">{loadError || "Animal not found."}</Alert>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          component={RouterLink}
          to={`/animals${animalsListQs}`}
        >
          Back to animals
        </Button>
      </Box>
    );
  }

  const profileHref = `/animal/${loadedAnimal.id}${profileQs}`;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ mb: 2, flexWrap: "wrap" }}
        >
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={`/animals${animalsListQs}`}
          >
            Animals
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={profileHref}
          >
            {loadedAnimal.alias || loadedAnimal.tag_number}
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            component="h1"
            sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
          >
            Edit animal
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              variant="outlined"
              component={RouterLink}
              to={profileHref}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              View profile
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to={`/animals${animalsListQs}`}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Animal list
            </Button>
          </Stack>
        </Box>

        <Box sx={{ display: "grid", gap: 2 }}>
          <Section title="Basic information">
            <Grid container spacing={2} direction="column">
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  id="tag_number"
                  name="tag_number"
                  label="Tag number *"
                  value={formik.values.tag_number}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.tag_number && Boolean(formik.errors.tag_number)
                  }
                  helperText={
                    formik.touched.tag_number && formik.errors.tag_number
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  name="alias"
                  label="Name / alias"
                  value={formik.values.alias}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={breedOptions}
                  value={formik.values.breed}
                  onChange={(_, newValue) =>
                    formik.setFieldValue("breed", newValue ?? "")
                  }
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input" || reason === "clear") {
                      formik.setFieldValue("breed", newInputValue ?? "");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="breed"
                      label="Breed *"
                      error={formik.touched.breed && Boolean(formik.errors.breed)}
                      helperText={
                        formik.touched.breed && formik.errors.breed
                          ? formik.errors.breed
                          : "Choose a breed used on this farm or type a new one."
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  label="Date of birth (optional)"
                  value={formik.values.dob}
                  onChange={(date) => formik.setFieldValue("dob", date)}
                  maxDate={new Date()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={formik.touched.dob && Boolean(formik.errors.dob)}
                      helperText={formik.touched.dob && formik.errors.dob}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  name="pattern_markings"
                  label="Pattern / markings"
                  value={formik.values.pattern_markings}
                  onChange={formik.handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  name="description"
                  label="Description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Characteristics">
            <Grid container spacing={2} direction="column">
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={formik.touched.gender && Boolean(formik.errors.gender)}
                >
                  <InputLabel>Gender *</InputLabel>
                  <Select
                    name="gender"
                    label="Gender *"
                    value={formik.values.gender}
                    onChange={formik.handleChange}
                  >
                    {GENDER_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formik.touched.gender && formik.errors.gender}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.category && Boolean(formik.errors.category)
                  }
                >
                  <InputLabel>Category *</InputLabel>
                  <Select
                    name="category"
                    label="Category *"
                    value={formik.values.category}
                    onChange={formik.handleChange}
                  >
                    {CATEGORY_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formik.touched.category && formik.errors.category}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={formik.touched.status && Boolean(formik.errors.status)}
                >
                  <InputLabel>Status *</InputLabel>
                  <Select
                    name="status"
                    label="Status *"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formik.touched.status && formik.errors.status}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  name="weight"
                  label="Weight (kg)"
                  type="number"
                  value={formik.values.weight}
                  onChange={formik.handleChange}
                  error={formik.touched.weight && Boolean(formik.errors.weight)}
                  helperText={
                    formik.touched.weight && formik.errors.weight
                      ? formik.errors.weight
                      : "Optional."
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={kraalOptions}
                  value={formik.values.kraal_name}
                  onChange={(_, newValue) =>
                    formik.setFieldValue("kraal_name", newValue ?? "")
                  }
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input" || reason === "clear") {
                      formik.setFieldValue("kraal_name", newInputValue ?? "");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="kraal_name"
                      label="Kraal / pen"
                      helperText="Pick an existing kraal or type a new name."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formik.values.is_active)}
                      onChange={(e) =>
                        formik.setFieldValue("is_active", e.target.checked)
                      }
                      name="is_active"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Tags">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Suggestions from your organization. Add new tags with Enter.
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={tagSuggestions}
              value={formik.values.tags}
              onChange={(_, newValue) =>
                formik.setFieldValue("tags", newValue)
              }
              renderTags={(value, getTagProps) =>
                value.map((opt, i) => (
                  <Chip key={i} label={opt} {...getTagProps({ index: i })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Tags"
                  placeholder="Type and press Enter"
                />
              )}
            />
          </Section>

          <Section title="Parent information (optional)">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search by tag or alias. Your selection is kept when results clear.
            </Typography>
            <Grid container spacing={2} direction="column">
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  value={damSelected}
                  options={damOptionsMerged}
                  loading={damLoading}
                  onChange={(_, val) => {
                    formik.setFieldValue("dam_id", val?.id ?? null);
                    setDamSelected(val ?? null);
                  }}
                  onInputChange={(_, val, reason) => {
                    if (reason === "input") setDamQuery(val);
                  }}
                  filterOptions={(x) => x}
                  getOptionLabel={getParentLabel}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dam (mother)"
                      placeholder="Search…"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {damLoading ? (
                              <CircularProgress size={18} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  value={sireSelected}
                  options={sireOptionsMerged}
                  loading={sireLoading}
                  onChange={(_, val) => {
                    formik.setFieldValue("sire_id", val?.id ?? null);
                    setSireSelected(val ?? null);
                  }}
                  onInputChange={(_, val, reason) => {
                    if (reason === "input") setSireQuery(val);
                  }}
                  filterOptions={(x) => x}
                  getOptionLabel={getParentLabel}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sire (father)"
                      placeholder="Search…"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {sireLoading ? (
                              <CircularProgress size={18} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Section>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              component={RouterLink}
              to={profileHref}
              disabled={formik.isSubmitting}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={formik.handleSubmit}
              disabled={formik.isSubmitting}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {formik.isSubmitting ? "Saving…" : "Save changes"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
